"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Confetti from "react-confetti";
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gogive/design-system";
import { useAccount } from "@/hooks/useAccount";
import { useAuth } from "@/providers/auth.provider";
import { useFundraisingCampaigns } from "@/hooks/useFundraisingCampaigns";
import { useStripeConnectLink } from "@/hooks/useStripeConnectLink";
import { getCampaignPublicFormUrl } from "@/lib/campaign.utils";
import {
  isStripeReturnCallback,
  stripeConnectCallbackUrls,
} from "@/lib/stripe-return";
import {
  syncStripePaymentProcessor,
  type StripePaymentProcessorStatus,
} from "@/services/account.service";

function useWindowSize() {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const update = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return size;
}

type ViewState =
  | "loading"
  | "connected"
  | "pending"
  | "incomplete"
  | "expired"
  | "error";

export function StripeReturnDialog() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { refreshMe } = useAuth();
  const { refresh: refreshAccount } = useAccount();

  const urlError =
    searchParams.get("error_description") ?? searchParams.get("error");
  const expiredLink = searchParams.get("state") === "refresh";
  const active = isStripeReturnCallback(searchParams);

  const [open, setOpen] = useState(active);
  const [status, setStatus] = useState<StripePaymentProcessorStatus | null>(
    null
  );
  const [syncError, setSyncError] = useState<string | null>(urlError);
  const [synced, setSynced] = useState(false);

  useEffect(() => {
    if (active) setOpen(true);
  }, [active]);

  const needsReconnect =
    !!urlError ||
    expiredLink ||
    (!!status &&
      !status.chargesEnabled &&
      (status.requirementsDue?.length ?? 0) > 0);

  const { stripeConnectLink, isLoading: linkLoading } = useStripeConnectLink(
    open && needsReconnect,
    stripeConnectCallbackUrls
  );

  useEffect(() => {
    if (!open || !active) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await syncStripePaymentProcessor();
        if (cancelled) return;
        setStatus(result);
        await Promise.all([refreshMe(), refreshAccount()]);
        if (cancelled) return;
        setSynced(true);
      } catch (e) {
        if (cancelled) return;
        setSyncError((e as Error).message);
        setSynced(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, active, refreshAccount, refreshMe]);

  const viewState: ViewState = useMemo(() => {
    if (urlError) return "error";
    if (!synced) return "loading";
    if (expiredLink) return "expired";
    if (syncError) return "error";
    if (!status) return "error";
    if (status.chargesEnabled) return "connected";
    if (status.detailsSubmitted) return "pending";
    return "incomplete";
  }, [synced, urlError, expiredLink, syncError, status]);

  const close = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen && isStripeReturnCallback(searchParams)) {
      router.replace("/");
    }
  };

  const content = {
    loading: {
      icon: <Loader2 className="h-6 w-6 animate-spin" />,
      title: "Checking Stripe",
      body: "Syncing your Stripe account status…",
    },
    connected: {
      icon: <CheckCircle2 className="h-6 w-6" />,
      title: "Congratulations!",
      body: "You can now safely receive donations.",
    },
    pending: {
      icon: <ShieldCheck className="h-6 w-6" />,
      title: "Stripe is reviewing your details",
      body: "You’ve submitted your information. Charges will enable once Stripe finishes review.",
    },
    incomplete: {
      icon: <AlertTriangle className="h-6 w-6" />,
      title: "Stripe setup is incomplete",
      body: "Finish connecting Stripe so you can accept donations.",
    },
    expired: {
      icon: <CreditCard className="h-6 w-6" />,
      title: "Your Stripe link expired",
      body: "Generate a fresh Connect link to continue setup.",
    },
    error: {
      icon: <AlertTriangle className="h-6 w-6" />,
      title: "Couldn’t connect Stripe",
      body:
        urlError ??
        syncError ??
        "Something went wrong. You can try connecting again.",
    },
  }[viewState];

  const showReconnect =
    viewState === "incomplete" ||
    viewState === "expired" ||
    viewState === "error";

  if (!open) return null;

  return (
    <>
      {viewState === "connected" ? <ConnectedConfetti /> : null}
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="items-center text-center sm:text-center">
            <div className="mx-auto rounded-full bg-primary/10 p-3 text-primary">
              {content.icon}
            </div>
            <DialogTitle className="text-center">{content.title}</DialogTitle>
            <DialogDescription className="text-center">
              {content.body}
            </DialogDescription>
          </DialogHeader>
          {viewState !== "loading" ? (
            <DialogFooter className="sm:justify-center">
              {viewState === "connected" ? (
                <ConnectedActions onDone={() => close(false)} />
              ) : (
                <>
                  {showReconnect ? (
                    <Button
                      asChild={!!stripeConnectLink?.url}
                      disabled={linkLoading || !stripeConnectLink?.url}
                    >
                      {stripeConnectLink?.url ? (
                        <a href={stripeConnectLink.url} rel="noreferrer">
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Connect Stripe
                        </a>
                      ) : (
                        <span>
                          {linkLoading ? "Preparing link…" : "Connect Stripe"}
                        </span>
                      )}
                    </Button>
                  ) : null}
                  <Button
                    onClick={() => close(false)}
                    type="button"
                    variant={showReconnect ? "outline" : "default"}
                  >
                    Close
                  </Button>
                </>
              )}
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ConnectedConfetti() {
  const { width, height } = useWindowSize();
  if (width === 0 || height === 0) return null;
  return (
    <Confetti
      width={width}
      height={height}
      recycle={false}
      numberOfPieces={320}
      gravity={0.18}
      style={{
        pointerEvents: "none",
        position: "fixed",
        inset: 0,
        zIndex: 100,
      }}
    />
  );
}

function ConnectedActions({ onDone }: { onDone: () => void }) {
  const { campaigns } = useFundraisingCampaigns({
    initialPage: 0,
    limit: 20,
  });
  const donateCampaign =
    campaigns.find((campaign) => campaign.isDefault) ?? campaigns[0];
  const donateUrl = donateCampaign
    ? getCampaignPublicFormUrl(donateCampaign)
    : "/campaigns";

  return (
    <>
      <Button asChild>
        <a
          href={donateUrl}
          rel="noreferrer"
          target={donateCampaign ? "_blank" : undefined}
        >
          Make a first donation
        </a>
      </Button>
      <Button onClick={onDone} type="button" variant="outline">
        Done
      </Button>
    </>
  );
}
